import { DEFAULT_LOCALE, LOCALES } from './constants';

/** Auth/public path suffixes (without locale prefix) */
export const PUBLIC_AUTH_PATHS = [
  '/login',
  '/register',
  '/register-2',
  '/register-3',
  '/recover-password',
  '/set-password',
  '/mfa',
];

export function isLocale(segment) {
  return LOCALES.includes(segment);
}

export function getLocaleFromPathname(pathname) {
  if (!pathname) {
    return null;
  }
  const segment = pathname.split('/').filter(Boolean)[0];
  return isLocale(segment) ? segment : null;
}

/** Remove leading /:locale from pathname; returns path starting with / */
export function stripLocale(pathname) {
  if (!pathname) {
    return '/';
  }
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length && isLocale(parts[0])) {
    const rest = parts.slice(1).join('/');
    return rest ? `/${rest}` : '/';
  }
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

/**
 * Build a locale-prefixed path for public routes.
 * @param {string} locale
 * @param {string} path - e.g. '/login' or 'login' or '/recover-password?x=1'
 */
export function withLocale(locale, path = '/') {
  const safeLocale = isLocale(locale) ? locale : DEFAULT_LOCALE;
  let pathname = path || '/';
  let search = '';
  let hash = '';

  const hashIndex = pathname.indexOf('#');
  if (hashIndex !== -1) {
    hash = pathname.slice(hashIndex);
    pathname = pathname.slice(0, hashIndex);
  }
  const searchIndex = pathname.indexOf('?');
  if (searchIndex !== -1) {
    search = pathname.slice(searchIndex);
    pathname = pathname.slice(0, searchIndex);
  }

  pathname = stripLocale(pathname.startsWith('/') ? pathname : `/${pathname}`);
  if (pathname === '/') {
    return `/${safeLocale}/login${search}${hash}`;
  }
  return `/${safeLocale}${pathname}${search}${hash}`;
}

export function isPublicAuthPath(pathname) {
  const stripped = stripLocale(pathname);
  if (stripped === '/' || stripped === '') {
    // bare / or /en/ treated as login entry on public side
    return true;
  }
  return PUBLIC_AUTH_PATHS.some(
    (p) => stripped === p || stripped.startsWith(`${p}/`)
  );
}

export function isPublicLocaleRoute(pathname) {
  return Boolean(getLocaleFromPathname(pathname));
}

/** Paths authenticated users should not stay on (locale-aware) */
export function isRestrictedAuthPath(pathname) {
  const stripped = stripLocale(pathname);
  return [
    '/mfa',
    '/register',
    '/register-2',
    '/register-3',
    '/set-password',
    '/recover-password',
    '/login',
  ].includes(stripped);
}
